import {
  IonButton,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonSearchbar,
  IonTitle,
  IonToolbar,
  useIonToast,
} from '@ionic/react';
import { useUsers } from '@lidea/shared/data-access/users';
import { User } from '@lidea/shared/util/types';
import { useDebouncedState } from '@mantine/hooks';
import { checkmarkCircle } from 'ionicons/icons';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SetupCacheControls } from '../../hooks/setup-cache-controls';
import ui from '../../offline-ui.module.css';
import {
  getOfflineScopeSession,
  OfflineScopeSession,
  setOfflineScopeSession,
} from '../../sync-job/offline-scope-session';

const CDS_ROLES = new Set(['CDS', 'CDSA']);

export type OfflineScopePickerProps = {
  userRole: string;
  campaignId: number;
  onClose: () => void;
  onScopeSaved?: () => void;
  onSyncStarted?: () => void;
  startAutoSync: SetupCacheControls['startAutoSync'];
  /** If set, pre-select this manager when opening the picker (change scope). */
  initialScope?: OfflineScopeSession | null;
};

export function OfflineScopePicker({
  userRole,
  campaignId,
  onClose,
  onScopeSaved,
  onSyncStarted,
  startAutoSync,
  initialScope,
}: OfflineScopePickerProps) {
  const { t } = useTranslation(['common'], { useSuspense: false });
  const [present] = useIonToast();
  const [searchQuery, setSearchQuery] = useDebouncedState('', 300);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<User | null>(null);

  const usersQuery = useUsers({ page, q: searchQuery });

  const cdsUsers = useMemo(() => {
    const results = usersQuery.data?.results ?? [];
    return results.filter((u) => CDS_ROLES.has(u.role));
  }, [usersQuery.data?.results]);

  const scopeToMatch = initialScope ?? getOfflineScopeSession();

  useEffect(() => {
    if (!scopeToMatch || selected) {
      return;
    }
    const match = cdsUsers.find((u) => u.id === scopeToMatch.managerId);
    if (match) {
      setSelected(match);
    }
  }, [cdsUsers, scopeToMatch, selected]);

  const persistScope = (user: User): OfflineScopeSession => {
    const scope: OfflineScopeSession = {
      managerId: user.id,
      managerLabel: `${user.first_name} ${user.last_name}`,
    };
    setOfflineScopeSession(scope);
    return scope;
  };

  const handleSaveOnly = () => {
    if (!selected) {
      return;
    }
    persistScope(selected);
    onScopeSaved?.();
    present({
      message: t('common:sync_job.scope_saved'),
      duration: 2000,
      position: 'bottom',
    });
    onClose();
  };

  const handleSaveAndSync = async () => {
    if (!selected) {
      return;
    }
    const scope = persistScope(selected);
    onScopeSaved?.();
    const res = await startAutoSync({
      campaignId,
      userRole,
      managerId: scope.managerId,
      managerLabel: scope.managerLabel,
    });
    if (res.setupCompleted) {
      onSyncStarted?.();
      onClose();
    }
  };

  const hasNext = !!usersQuery.data?.next;

  return (
    <>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t('common:sync_job.pick_cds_title')}</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <IonSearchbar
            placeholder={t('common:searchbar_placeholder') as string}
            onIonInput={(e) => {
              setSearchQuery(e.detail.value ?? '');
              setPage(1);
            }}
          />
        </IonToolbar>
      </IonHeader>
      <IonContent className={ui.page}>
        <div className="ion-padding">
        <p className={ui.hint}>{t('common:sync_job.pick_cds_hint')}</p>
        <IonList>
          {cdsUsers.map((user) => (
            <IonItem
              key={user.id}
              button
              detail={false}
              onClick={() => setSelected(user)}
            >
              {selected?.id === user.id ? (
                <IonIcon
                  icon={checkmarkCircle}
                  slot="start"
                  color="primary"
                />
              ) : null}
              <IonLabel>
                <h2>
                  {user.first_name} {user.last_name}
                </h2>
                <p>{user.role}</p>
              </IonLabel>
            </IonItem>
          ))}
        </IonList>
        {hasNext ? (
          <IonButton
            expand="block"
            fill="clear"
            disabled={usersQuery.isFetching}
            onClick={() => setPage((p) => p + 1)}
          >
            {t('common:sync_job.load_more_users')}
          </IonButton>
        ) : null}
        {usersQuery.isError ? (
          <p>{t('common:unknown_error')}</p>
        ) : null}
        </div>
      </IonContent>
      <IonFooter>
        <IonToolbar>
          <IonButton
            color="medium"
            expand="block"
            fill="outline"
            onClick={onClose}
          >
            {t('common:cancel')}
          </IonButton>
          <IonButton
            disabled={!selected}
            expand="block"
            fill="outline"
            onClick={handleSaveOnly}
          >
            {t('common:sync_job.save_scope')}
          </IonButton>
          <IonButton disabled={!selected} expand="block" onClick={handleSaveAndSync}>
            {t('common:sync_job.save_scope_and_sync')}
          </IonButton>
        </IonToolbar>
      </IonFooter>
    </>
  );
}

export default OfflineScopePicker;
